/**
 * Color Utility Service
 * 
 * Provides pure functions for color manipulation and transformation
 */

/**
 * Adjust the brightness of a hex color
 * @param color - Hex color (with or without #)
 * @param amount - Amount to adjust (-255 to 255, negative = darker, positive = brighter)
 * @returns Adjusted hex color
 */
export const adjustColorBrightness = (color: string, amount: number): string => {
  const usePound = color[0] === '#';
  const col = usePound ? color.slice(1) : color;
  const num = parseInt(col, 16);
  
  let r = ((num >> 16) & 0xFF) + amount;
  let g = ((num >> 8) & 0xFF) + amount;
  let b = (num & 0xFF) + amount;
  
  // Clamp values between 0 and 255
  r = Math.max(0, Math.min(255, r));
  g = Math.max(0, Math.min(255, g));
  b = Math.max(0, Math.min(255, b));
  
  return (usePound ? '#' : '') + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
};

/**
 * Convert hex color to RGB values
 */
export const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

/**
 * Convert RGB values to hex color
 */
export const rgbToHex = (r: number, g: number, b: number): string => {
  return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};

/**
 * Calculate relative luminance of a color (for contrast calculations)
 * Based on WCAG 2.0 formula
 */
export const getRelativeLuminance = (hex: string): number => {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  
  const rsRGB = rgb.r / 255;
  const gsRGB = rgb.g / 255;
  const bsRGB = rgb.b / 255;
  
  const r = rsRGB <= 0.03928 ? rsRGB / 12.92 : Math.pow((rsRGB + 0.055) / 1.055, 2.4);
  const g = gsRGB <= 0.03928 ? gsRGB / 12.92 : Math.pow((gsRGB + 0.055) / 1.055, 2.4);
  const b = bsRGB <= 0.03928 ? bsRGB / 12.92 : Math.pow((bsRGB + 0.055) / 1.055, 2.4);
  
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/**
 * Calculate contrast ratio between two colors
 * Returns a value between 1 and 21
 */
export const getContrastRatio = (color1: string, color2: string): number => {
  const lum1 = getRelativeLuminance(color1);
  const lum2 = getRelativeLuminance(color2);
  
  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);
  
  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Check if a color combination meets WCAG AA standards
 */
export const meetsWCAGAA = (foreground: string, background: string, isLargeText = false): boolean => {
  const ratio = getContrastRatio(foreground, background);
  return isLargeText ? ratio >= 3 : ratio >= 4.5;
};

/**
 * Check if a color combination meets WCAG AAA standards
 */
export const meetsWCAGAAA = (foreground: string, background: string, isLargeText = false): boolean => {
  const ratio = getContrastRatio(foreground, background);
  return isLargeText ? ratio >= 4.5 : ratio >= 7;
};

/**
 * Validate if a string is a valid hex color
 */
export const isValidHexColor = (color: string): boolean => {
  return /^#?([a-f\d]{3}|[a-f\d]{6})$/i.test(color);
};

/**
 * Normalize hex color (ensure it has # and is 6 digits)
 */
export const normalizeHexColor = (color: string): string => {
  let normalized = color.trim();
  
  // Add # if missing
  if (!normalized.startsWith('#')) {
    normalized = '#' + normalized;
  }
  
  // Expand 3-digit to 6-digit
  if (normalized.length === 4) {
    normalized = '#' + normalized[1] + normalized[1] + normalized[2] + normalized[2] + normalized[3] + normalized[3];
  }
  
  return normalized.toLowerCase();
};

/**
 * Generate a linear gradient CSS string
 */
export const generateGradient = (color1: string, color2: string, direction = 'to right'): string => {
  return `linear-gradient(${direction}, ${color1}, ${color2})`;
};

